import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, map, mergeMap, of, takeUntil } from 'rxjs';
import { clearAllState } from '../../../core/store/actions/app.actions';
import { patientSwitchInitiated } from '../../../user/store/actions/user.actions';
import { selectPatientId } from '../../../user/store/selectors/user.selectors';
import { PatientImmunizationsService } from '../../services/patient-immunizations.service';
import {
  getPatientImmunizations,
  getPatientImmunizationsFailure,
  getPatientImmunizationsSuccess,
  recordImmunizationPrintEvent,
  recordImmunizationPrintEventFailure,
  recordImmunizationPrintEventSuccess,
} from '../actions/immunization.actions';

@Injectable()
export class ImmunizationEffects {
  private actions$ = inject(Actions);
  private patientImmunizationsService = inject(PatientImmunizationsService);
  private store = inject(Store);
  private readonly cancelRequest$ = this.actions$.pipe(ofType(patientSwitchInitiated, clearAllState));

  getPatientImmunizations$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getPatientImmunizations),
      concatLatestFrom(() => [this.store.select(selectPatientId)]),
      mergeMap(([, patientId]) => {
        return this.patientImmunizationsService.getPatientImmunizations(patientId).pipe(
          takeUntil(this.cancelRequest$),
          map(patientImmunizations => {
            patientImmunizations?.forEach(group => {
              group.totalRecordCount = group.immunizations.length;
              group.showAll = false;
            });
            return getPatientImmunizationsSuccess({ patientImmunizations });
          }),
          catchError(err => {
            return of(getPatientImmunizationsFailure({ error: err.error?.errors }));
          })
        );
      })
    );
  });

  recordPrintEvent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(recordImmunizationPrintEvent),
      mergeMap(({ patientId, patientImmunizationIds }) =>
        this.patientImmunizationsService.postPrintEvent(patientId, patientImmunizationIds).pipe(
          map(() => recordImmunizationPrintEventSuccess()),
          catchError(err => of(recordImmunizationPrintEventFailure({ error: err })))
        )
      )
    );
  });
}
